<template>
    <th @click.stop="updateSort" :data-label="dataLabel">
        <slot name="th">
            <div class="column-label">
                <span>{{ label }}</span>
                <div class="arrow unselectable" v-if="sortable">
                    <i :class="{ 'material-icons': true, active: upArrowActive }">arrow_drop_up</i>
                    <i :class="{ 'material-icons': true, active: downArrowActive }">arrow_drop_down</i>
                </div>
            </div>
        </slot>
    </th>
</template>

<script>
function defaultFormatter(valueFunction) {
    return row => {
        var raw = valueFunction(row)

        if (raw !== null && raw !== undefined) {
            return raw.toString()
        }

        return ''
    }
}

export default {
    name: 'MtColumn',
    props: {
        label: {
            type: String,
            default: '',
        },
        field: {
            type: String,
            required: false,
        },
        sortable: {
            type: Boolean,
            default: false,
        },
        unlinked: {
            type: Boolean,
            default: false,
        },
        format: {
            type: Function,
            default: null,
        },
        sorter: {
            type: [Function, Object],
            required: false,
        },
        title: {
            type: Boolean,
            default: false,
        },
        titleProvider: {
            type: Function,
            required: false,
        },
    },
    inject: ['currentSort', 'onSortChange', 'table'],
    mounted() {
        this.table.updateColumns()
    },
    beforeDestroy() {
        this.table.updateColumns()
    },
    computed: {
        dataLabel() {
            return this.label || this.field
        },
        sorted() {
            return this.currentSort.field === this.field
        },
        upArrowActive() {
            return this.sorted && this.currentSort.direction == 'asc'
        },
        downArrowActive() {
            return this.sorted && this.currentSort.direction == 'desc'
        },
        sortDirection() {
            return this.currentSort.direction
        },
        autolink() {
            return !this.unlinked
        },
        fieldValue() {
            var field = this.field

            if (!field) {
                return row => row
            }

            if (field.indexOf('.') > -1) {
                var fields = field.split('.')
                return row => fields.reduce((p, c) => (p !== null && p !== undefined ? p[c] : p), row)
            }

            return row => row[field]
        },
        formattedValue() {
            var format = this.format
            var value = this.fieldValue

            if (format) {
                return row => format(value(row), row)
            }

            return defaultFormatter(value)
        },
        showTitle() {
            return this.title || this.titleProvider
        },
        titleCallback() {
            return this.titleProvider || this.formattedValue
        },
    },
    methods: {
        updateSort() {
            if (!this.sortable) {
                return
            }

            if (this.sorted && this.sortDirection != 'desc') {
                this.onSortChange({ field: this.field, direction: 'desc' })
            } else {
                this.onSortChange({ field: this.field, direction: 'asc' })
            }
        },
        applySort(rows, direction) {
            return rows.sort(this._comparer(direction))
        },
        renderContent(row, link, index, rows) {
            // scoped slot (uses the row as a prop)
            if (this.$scopedSlots.default) {
                return this.$scopedSlots.default({ row, link, index, rows })
            }

            // unscoped slot (some kind of dynamic content that doesn't change per row)
            if (this.$slots.default) {
                return this.$slots.default
            }

            return this.renderField(row, link)
        },
        renderField(row, link) {
            var value = this.formattedValue(row)

            if (this.autolink && link) {
                value = this.$createElement('router-link', { props: { to: link(row) } }, [value])
            }

            return value
        },
        renderTableCell(row, link, index, rows) {
            const classes = []

            const staticClass = this.$vnode.data.staticClass
            const dynamicClass = this.$vnode.data.class

            if (staticClass !== undefined) {
                classes.push(...staticClass.split(' '))
            }

            if (Array.isArray(dynamicClass)) {
                classes.push(...dynamicClass)
            } else if (dynamicClass !== undefined) {
                classes.push(dynamicClass)
            }

            return this.$createElement(
                'td',
                {
                    attrs: {
                        'data-label': this.dataLabel,
                        title: this._getTitle(row),
                    },
                    class: classes.join(' '),
                },
                [this.renderContent(row, link, index, rows)]
            )
        },
        _getTitle(row) {
            if (!this.showTitle) {
                return null
            }

            const callback = this.titleCallback
            return callback(row)
        },
        _comparer(direction) {
            if (this.sorter && this.sorter.create) {
                return this.sorter.create(this.fieldValue, direction)
            }

            const inner = this.sorter || this._defaultSorter()

            if (direction == 'desc') {
                return (lhs, rhs) => inner(rhs, lhs, this.fieldValue)
            }

            return (lhs, rhs) => inner(lhs, rhs, this.fieldValue)
        },
        _defaultSorter() {
            return (lhs, rhs) => {
                lhs = this.fieldValue(lhs)
                rhs = this.fieldValue(rhs)

                if (typeof lhs === 'string' || typeof rhs === 'string') {
                    return (lhs || '').localeCompare(rhs || '', 'en', { sensitivity: 'base' })
                }

                return (lhs || 0) - (rhs || 0)
            }
        },
    },
}
</script>
<style>
.mt-table {
    th.multiline-header {
        padding: 0;

        > div:first-child {
            align-items: center;
            line-height: 1.2;
            min-height: 40px;
            padding: 0 8px 0 16px;

            > span {
                white-space: initial;
            }
        }
    }
}

div.arrow {
    border-radius: 12px;
    height: 24px;
    width: 24px;
    position: relative;

    .material-icons {
        line-height: 12px;
        cursor: pointer;
        left: 0px;
        position: absolute;
        font-size: var(--font-size-breadcrumbs);
        color: var(--color-primary-200);

        &:first-child {
            top: 2px;
        }
        &:last-child {
            bottom: 2px;
        }

        &.active {
            color: var(--color-primary-600);
        }
    }

    &:hover {
        background-color: var(--color-primary-200);
    }
}
</style>
